import { useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { parseContentDispositionFilename } from '../domain/downloads';

const EXCEL_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * Download a file (an Excel workbook unless `accept` says otherwise) from an
 * authenticated API path and hand it to the browser. The request helpers parse
 * JSON, so a binary download goes through `fetch` with the session token instead.
 * Rejects on a non-2xx answer.
 */
export default function useFileDownload() {
    const { token, API_BASE_URL } = useAuth();

    return useCallback(
        async (path: string, fallbackFilename: string, accept = EXCEL_MIME_TYPE): Promise<void> => {
            const response = await fetch(`${API_BASE_URL}${path}`, {
                headers: {
                    ...(token && { Authorization: `Bearer ${token}` }),
                    Accept: accept,
                },
            });
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download =
                parseContentDispositionFilename(response.headers.get('Content-Disposition')) ??
                fallbackFilename;
            anchor.click();
            URL.revokeObjectURL(url);
        },
        [API_BASE_URL, token],
    );
}
